import { useEffect, type RefObject } from 'react';

interface UseClickOutsideOptions {
  /** Selector for elements that should be ignored (clicks on these won't trigger the callback) */
  ignoreSelector?: string;
  /** Whether the hook is enabled */
  enabled?: boolean;
}

/**
 * Hook that triggers a callback when clicking outside of the referenced element
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  callback: () => void,
  options: UseClickOutsideOptions = {}
) {
  const { ignoreSelector, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is inside the ref element
      if (ref.current && ref.current.contains(event.target as Node)) {
        return;
      }

      // Check if click is on an ignored element
      if (ignoreSelector) {
        const target = event.target as HTMLElement;
        if (target.closest(ignoreSelector)) {
          return;
        }
      }

      callback();
    };

    // Delay adding listener to avoid immediate close from the click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback, ignoreSelector, enabled]);
}
